"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, } from "@workspace/ui/components/dialog";
import { Switch } from "@workspace/ui/components/switch";
import Logo from "@workspace/ui/icons/logo";
import ChevronDown from "@workspace/ui/icons/chevron-down";
import { Button } from "@workspace/ui/components/button";
import { sendEvent } from "../events/utils";
import { getAllowedConsent, getDeniedConsent, INTERACTION_TYPE } from "../utils/cookieUtils";
const COOKIE_CATEGORIES = [
    {
        id: "necessary",
        name: "Necessary",
        required: true,
        details: "Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data.",
    },
    {
        id: "performance",
        name: "Performance",
        required: false,
        details: "We use these cookies to provide statistical information about our website - they are used for performance measurement and improvement.This category is also known as Analytics. Activities like page visits counting, page loading speed, bounce rate and technologies used to access our site are included in this category",
    },
    {
        id: "functional",
        name: "Functional",
        required: false,
        details: "We use these cookies to enhance functionality and allow for personalisation, such as live chats, videos and the use of social media.These cookies can be set by ourselves or by our third party service providers, whose digital services we have added. If you do not allow these cookies, some of these functionalities may not work properly.",
    },
    {
        id: "advertising",
        name: "Advertising",
        required: false,
        details: "These cookies are set through our site by our advertising partners. These cookies can be used by third party companies for creating a basic profile of your interests and show you relevant ads on other websites. They identify your browser and your device. If you decide to disallow these cookies, you will not be tracked by our targeted advertising across other websites.",
    },
];
export function CookiePreferencesModal({ open, onOpenChange, onSave, onAcceptAll, onRejectAll, consent, setConsent, }) {
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [localConsent, setLocalConsent] = useState(consent);
    // Reset local state when modal opens
    useEffect(() => {
        if (open) {
            setLocalConsent(consent);
            sendEvent("cookie_consent_popup_openend");
        }
    }, [open, consent]);
    const toggleCategory = (categoryId) => {
        if (categoryId === "necessary")
            return; // Can't toggle necessary cookies
        setLocalConsent((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };
    const toggleExpanded = (categoryId) => {
        setExpandedCategories((prev) => prev.includes(categoryId)
            ? prev.filter((id) => id !== categoryId)
            : [...prev, categoryId]);
    };
    const handleSave = () => {
        const consentWithInteractionType = {
            ...localConsent,
            interactionType: INTERACTION_TYPE.SAVE_SETTINGS,
        };
        onSave(consentWithInteractionType);
        setConsent(consentWithInteractionType);
        onOpenChange(false);
    };
    const handleAcceptAll = () => {
        const allowedConsent = getAllowedConsent();
        onAcceptAll(allowedConsent);
        setConsent(allowedConsent);
        setLocalConsent(allowedConsent);
        onOpenChange(false);
        sendEvent("popup_accept_all_button_clicked");
    };
    const handleRejectAll = () => {
        const deniedConsent = getDeniedConsent();
        onRejectAll(deniedConsent);
        setConsent(deniedConsent);
        setLocalConsent(deniedConsent);
        onOpenChange(false);
        sendEvent("popup_reject_all_button_clicked");
    };
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { className: "max-h-[90vh] overflow-y-auto p-6 flex flex-col gap-4 lg:gap-8 !max-w-[640px] lg:h-[540px]", children: [_jsx(Logo, { className: "h-9 w-24 shrink-0" }), _jsx(DialogHeader, { className: "text-start", children: _jsx(DialogTitle, { className: "text-[13px] font-normal text-start", id: "cookie-preferences-modal-title", "data-testid": "cookie-preferences-modal-title", children: "We would like your permission to use your data for the following purposes:" }) }), _jsx("div", { className: "space-y-4 flex-1 overflow-auto", children: COOKIE_CATEGORIES.map((category) => {
                        const isEnabled = localConsent[category.id];
                        const isExpanded = expandedCategories.includes(category.id);
                        return (_jsx("div", { children: _jsxs("div", { className: "py-4 pb-7 border-b border-b-gray-100", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("div", { className: "flex-1", children: _jsx("h3", { className: "font-bold text-base", id: `cookie-preferences-modal-category-${category.id}`, "data-testid": `cookie-preferences-modal-category-${category.id}`, children: category.name }) }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "flex items-center", children: _jsx(Switch, { checked: isEnabled, onCheckedChange: () => toggleCategory(category.id), disabled: category.required, "data-testid": `switch-${category.id}`, id: `switch-${category.id}` }) }), _jsx("button", { onClick: () => toggleExpanded(category.id), className: "text-gray-400 hover:text-gray-600 p-1 hover:cursor-pointer", "aria-label": `${isExpanded ? "Collapse" : "Expand"} ${category.name} details`, "data-testid": `toggle-${category.id}`, id: `toggle-${category.id}`, children: _jsx(ChevronDown, { className: `w-5 h-5 transform transition-transform ${isExpanded ? "rotate-180" : ""}` }) })] })] }), isExpanded && category.details && (_jsx("div", { className: "mt-3 pt-3 w-[85%]", children: _jsx("p", { className: "body-14 text-secondary", id: `cookie-preferences-modal-category-${category.id}-details`, "data-testid": `cookie-preferences-modal-category-${category.id}-details`, children: category.details }) }))] }) }, category.id));
                    }) }), _jsxs(DialogFooter, { className: "flex-col gap-3 w-full", children: [_jsx(Button, { onClick: handleSave, variant: "outline", className: "flex-1", "data-testid": "cookie-preferences-modal-save-settings", id: "cookie-preferences-modal-save-settings", children: "Save Settings" }), _jsx(Button, { onClick: handleRejectAll, variant: "outline", className: "flex-1", "data-testid": "cookie-preferences-modal-reject-all", id: "cookie-preferences-modal-reject-all", children: "Reject All" }), _jsx(Button, { onClick: handleAcceptAll, className: "flex-1", "data-testid": "cookie-preferences-modal-accept-all", id: "cookie-preferences-modal-accept-all", children: "Accept All" })] })] }) }));
}
